import type { IPublicClientApplication, AccountInfo } from '@azure/msal-browser'
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import type { AppConfig } from '../config'
import type {
  AccountUsageResponse,
  EnvironmentListQuery,
  EnvironmentListResponse,
  EnvironmentDetail,
  EnvironmentDeleteResponse,
  AccountEnvironmentsResponse,
  DeviceListResponse,
  TokenIssuanceResponse,
  PatchStatusResponse,
  MapAccountResponse,
  GrantResponse,
  EnvironmentStatus,
  MigrationLicenseType,
  ActivityResponse,
  ActivityQuery,
  AccountDetail,
  AccountListQuery,
  AccountListResponse,
  AdjustmentResponse,
  CreateAccountResponse,
  Entitlement,
  EntitlementEditResponse,
  ExpiryChangeResponse,
  IssueEnvironmentOptions,
  MigrationBalance,
  PrincipalClass,
  RenameAccountResponse,
} from './types'

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string | undefined,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// A thin, token-acquiring API client. Every call silently acquires an access
// token for the operator-console API scope (falling back to a redirect when
// interaction is required), then calls the Function App. The Function Apps'
// Easy Auth validates the token; their CORS allows this SPA's origin (ATC-053).
export class OperatorApi {
  constructor(
    private msal: IPublicClientApplication,
    private account: AccountInfo,
    private config: AppConfig,
  ) {}

  private async token(): Promise<string> {
    try {
      const result = await this.msal.acquireTokenSilent({
        scopes: [this.config.apiScope],
        account: this.account,
      })
      return result.accessToken
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        await this.msal.acquireTokenRedirect({ scopes: [this.config.apiScope], account: this.account })
      }
      throw err
    }
  }

  private async request<T>(
    baseUrl: string,
    method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
    path: string,
    body?: unknown,
  ): Promise<T> {
    const accessToken = await this.token()
    const headers: Record<string, string> = { Authorization: `Bearer ${accessToken}` }
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json'
    }
    const res = await fetch(`${baseUrl}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
    const payload = await res.json().catch(() => null)
    if (!res.ok) {
      const code = payload?.error?.code as string | undefined
      const message = (payload?.error?.message as string | undefined) ?? `Request failed (HTTP ${res.status})`
      throw new ApiError(res.status, code, message)
    }
    return payload?.data as T
  }

  private get<T>(baseUrl: string, path: string): Promise<T> {
    return this.request<T>(baseUrl, 'GET', path)
  }

  listEnvironments(query: EnvironmentListQuery = {}): Promise<EnvironmentListResponse> {
    const params = new URLSearchParams()
    if (query.status) params.set('status', query.status)
    if (query.search) params.set('search', query.search)
    if (query.accountId) params.set('accountId', query.accountId)
    if (query.pageSize) params.set('pageSize', String(query.pageSize))
    if (query.continuationToken) params.set('continuationToken', query.continuationToken)
    const qs = params.toString()
    return this.get<EnvironmentListResponse>(
      this.config.licensingBaseUrl,
      `/v1/environments${qs ? `?${qs}` : ''}`,
    )
  }

  getEnvironment(customerEnvironmentId: string): Promise<EnvironmentDetail> {
    return this.get<EnvironmentDetail>(
      this.config.licensingBaseUrl,
      `/v1/environments/${encodeURIComponent(customerEnvironmentId)}`,
    )
  }

  listAccountEnvironments(accountId: string): Promise<AccountEnvironmentsResponse> {
    return this.get<AccountEnvironmentsResponse>(
      this.config.licensingBaseUrl,
      `/v1/accounts/${encodeURIComponent(accountId)}/environments`,
    )
  }

  listDevices(customerEnvironmentId: string): Promise<DeviceListResponse> {
    return this.get<DeviceListResponse>(
      this.config.mintBaseUrl,
      `/v1/devices/${encodeURIComponent(customerEnvironmentId)}`,
    )
  }

  // --- Write actions (ATC-056). All hit Licensing. ---

  // Options object rather than a growing positional list: issuance now carries
  // up to five fields and the closed schema (Hotfix 038) REFUSES anything it
  // does not recognise, so building the body key-by-key is the only safe shape.
  // Optional fields are omitted entirely, never sent as `undefined` — that is
  // not a distinction the server can see, but building the object this way
  // keeps it from becoming one.
  issueEnvironment(options: IssueEnvironmentOptions): Promise<TokenIssuanceResponse> {
    // ATC-140: `customerName` is omitted entirely when absent, never sent as
    // `undefined`. The server's closed schema refuses an unrecognised field
    // but an explicit `undefined` would serialise away anyway — building the
    // body key-by-key keeps "not sent" and "sent as nothing" from becoming the
    // same thing by accident.
    const body: Record<string, unknown> = {}
    if (options.customerName) {
      body.customerName = options.customerName
    }
    if (options.entitledServices) {
      body.entitledServices = options.entitledServices
    }
    if (options.expiresAt) {
      body.expiresAt = options.expiresAt
    }
    if (options.principalClass) {
      body.principalClass = options.principalClass
    }
    if (options.customerEnvironmentId) {
      body.customerEnvironmentId = options.customerEnvironmentId
    }
    return this.request<TokenIssuanceResponse>(
      this.config.licensingBaseUrl,
      'POST',
      '/v1/customerEnvironments',
      body,
    )
  }

  // Mints the ATC-ADR-016 `atc_fd_` credential ON AN EXISTING environment: the
  // customerEnvironmentId is supplied rather than minted, because a second
  // GUID would be a second tenant. The server refuses a second flightdeck
  // credential with 409 `flightdeck_principal_already_issued`.
  issueFlightdeckCredential(args: {
    customerEnvironmentId: string
    // ATC-140. Optional, and normally omitted: the server INHERITS the label
    // from the environment's existing row, because the label belongs to the
    // environment and not to the credential. Passing one here would let the
    // two credentials on one environment carry different labels.
    customerName?: string
    entitledServices?: Entitlement[]
    expiresAt?: string
  }): Promise<TokenIssuanceResponse> {
    return this.issueEnvironment({
      customerName: args.customerName,
      customerEnvironmentId: args.customerEnvironmentId,
      principalClass: 'flightdeck',
      entitledServices: args.entitledServices,
      expiresAt: args.expiresAt,
    })
  }

  // ATC-142. Destroys the environment, every credential it holds, its registry
  // row and its account membership.
  //
  // NO OPTIONS ARGUMENT, and that is the point rather than an omission: the
  // server refuses any body at all, so there is deliberately nothing here that
  // could be mistaken for a force or cascade flag. An active environment is
  // refused (`environment_active`) and has to be suspended or revoked first.
  //
  // Positional-argument safety does not arise — there is one parameter, and it
  // is the id in the path. (Contrast `mapAccountEnvironment`, which takes a
  // named object because two same-typed positional strings are the shape that
  // let ATC-125's swapped arguments through.)
  deleteEnvironment(customerEnvironmentId: string): Promise<EnvironmentDeleteResponse> {
    return this.request<EnvironmentDeleteResponse>(
      this.config.licensingBaseUrl,
      'DELETE',
      `/v1/customerEnvironments/${encodeURIComponent(customerEnvironmentId)}`,
    )
  }

  setEnvironmentStatus(customerEnvironmentId: string, status: EnvironmentStatus): Promise<PatchStatusResponse> {
    return this.request<PatchStatusResponse>(
      this.config.licensingBaseUrl,
      'PATCH',
      `/v1/customerEnvironments/${encodeURIComponent(customerEnvironmentId)}`,
      { status },
    )
  }

  // `principalClass` is REQUIRED, and that is a correctness fix rather than an
  // addition. The server defaults an absent selector to `device`, so on an
  // environment holding both credentials this call would SILENTLY ROTATE THE
  // WRONG ONE -- re-keying the device fleet when the operator asked for the
  // Flight Deck credential, with no error anywhere. Required, not defaulted:
  // a default here would reproduce the server's default and hide the choice
  // at exactly the call site that has to make it.
  rotateEnvironmentToken(
    customerEnvironmentId: string,
    principalClass: PrincipalClass,
  ): Promise<TokenIssuanceResponse> {
    return this.request<TokenIssuanceResponse>(
      this.config.licensingBaseUrl,
      'PATCH',
      `/v1/customerEnvironments/${encodeURIComponent(customerEnvironmentId)}`,
      { rotateToken: true, principalClass },
    )
  }

  // ATC-074. Additive/subtractive, never a whole-list replacement — a
  // replacement makes accidental REMOVAL a one-typo operation on the set of
  // services a live credential may call.
  //
  // `principalClass` is ALWAYS sent, even when only one credential exists:
  // omitting it on a two-credential environment returns 409
  // `partition_ambiguous`, and "works until the customer gets a second
  // credential" is not a property worth shipping.
  editEntitlements(args: {
    customerEnvironmentId: string
    principalClass: PrincipalClass
    add?: string[]
    remove?: string[]
  }): Promise<EntitlementEditResponse> {
    const body: Record<string, unknown> = { principalClass: args.principalClass }
    if (args.add && args.add.length > 0) {
      body.addEntitlements = args.add
    }
    if (args.remove && args.remove.length > 0) {
      body.removeEntitlements = args.remove
    }
    return this.request<EntitlementEditResponse>(
      this.config.licensingBaseUrl,
      'PATCH',
      `/v1/customerEnvironments/${encodeURIComponent(args.customerEnvironmentId)}`,
      body,
    )
  }

  // ATC-121. TWO FIELDS ON THE WIRE, not one nullable one: `{ expiresAt }` sets
  // and `{ clearExpiry: true }` clears.
  //
  // `{ expiresAt: null }` is deliberately NOT the clear signal and must never
  // be sent. The server recognises the key but tests its VALUE, so a null falls
  // through to `patch_action_missing` — a legible 400 rather than a silent
  // no-op, which is the outcome that design was chosen to guarantee on a
  // revocation-adjacent operation. `JSON.stringify` dropping an `undefined`
  // would produce the same empty body and the same 400.
  //
  // The selector follows the STATUS precedent: absent means EVERY row, because
  // an expiry is a commercial term on the environment rather than a property of
  // one credential. Leaving the `atc_fd_` credential alive past the customer's
  // term is the failure that default exists to prevent.
  setEnvironmentExpiry(args: {
    customerEnvironmentId: string
    expiresAt: string | null
    principalClass?: PrincipalClass
  }): Promise<ExpiryChangeResponse> {
    const body: Record<string, unknown> =
      args.expiresAt === null ? { clearExpiry: true } : { expiresAt: args.expiresAt }
    if (args.principalClass) {
      body.principalClass = args.principalClass
    }
    return this.request<ExpiryChangeResponse>(
      this.config.licensingBaseUrl,
      'PATCH',
      `/v1/customerEnvironments/${encodeURIComponent(args.customerEnvironmentId)}`,
      body,
    )
  }

  // NAMED ARGUMENTS, deliberately, and this one is not a style preference.
  //
  // The previous signature was `(accountId, customerEnvironmentId,
  // accountName?)` -- three same-typed positional strings, two of them
  // interchangeable at the type level. `EnvironmentDetail.tsx` passed them the
  // other way round and TypeScript could not object, so every map from the
  // console wrote a row at `PartitionKey=<accountId>, RowKey=<cei>` -- the
  // inverse shape -- deleted rows in a normally-empty partition, and never
  // wrote the intended one. The mapping silently did not take: the page
  // reloaded still reading "Unmapped" right after a successful-looking POST.
  //
  // An object literal makes the two roles unswappable at the call site, which
  // is the only place the swap was expressible. See ATC-134 for the data
  // cleanup; the console does not attempt it.
  mapAccountEnvironment(args: {
    accountId: string
    customerEnvironmentId: string
    accountName?: string
  }): Promise<MapAccountResponse> {
    const body: Record<string, string> = { customerEnvironmentId: args.customerEnvironmentId }
    if (args.accountName) {
      body.accountName = args.accountName
    }
    return this.request<MapAccountResponse>(
      this.config.licensingBaseUrl,
      'POST',
      `/v1/accounts/${encodeURIComponent(args.accountId)}/environments`,
      body,
    )
  }

  // --- Accounts (ATC-124 / R-ATC-10) ---

  // The accountId is minted SERVER-SIDE as a GUID and a caller-supplied one is
  // refused, so there is no id parameter here by design: "type the identifier
  // from memory" is the failure the registry exists to remove.
  createAccount(accountName: string): Promise<CreateAccountResponse> {
    return this.request<CreateAccountResponse>(this.config.licensingBaseUrl, 'POST', '/v1/accounts', {
      accountName,
    })
  }

  // Keyset-paginated. Carries NO balance — that is an unbounded ledger
  // partition scan per row; the detail view fetches exactly one via
  // getAccountBalance.
  listAccounts(query: AccountListQuery = {}): Promise<AccountListResponse> {
    const params = new URLSearchParams()
    if (query.status) params.set('status', query.status)
    if (query.search) params.set('search', query.search)
    if (query.pageSize) params.set('pageSize', String(query.pageSize))
    if (query.continuationToken) params.set('continuationToken', query.continuationToken)
    const qs = params.toString()
    return this.get<AccountListResponse>(this.config.licensingBaseUrl, `/v1/accounts${qs ? `?${qs}` : ''}`)
  }

  // ATC-137. The registry's own record — the ONLY correct source of a
  // customer's name.
  //
  // CustomerDetail used to derive its title from the environment membership
  // rows instead, because until ATC-137 no endpoint exposed the registry row:
  //
  //     environments.find((e) => e.accountName)?.accountName ?? ''
  //
  // A customer with nothing mapped therefore rendered as "(unnamed customer)"
  // on its own detail page while the LIST — reading this same registry —
  // showed the name correctly. Worse, the membership copy is the PRE-registry
  // name field, and `Set-AtcAccountRegistryTouch` never overwrites a non-blank
  // registry name, so a different name entered on the map form produced two
  // permanent names for one customer, one per view. Do not reintroduce the
  // membership read as a name source.
  getAccount(accountId: string): Promise<AccountDetail> {
    return this.get<AccountDetail>(
      this.config.licensingBaseUrl,
      `/v1/accounts/${encodeURIComponent(accountId)}`,
    )
  }

  // ATC-137. Renames an account; the accountId is the RowKey and is immutable,
  // so it is a route parameter and never a body field — the server refuses one
  // in the body by name.
  renameAccount(accountId: string, accountName: string): Promise<RenameAccountResponse> {
    return this.request<RenameAccountResponse>(
      this.config.licensingBaseUrl,
      'PATCH',
      `/v1/accounts/${encodeURIComponent(accountId)}`,
      { accountName },
    )
  }

  getAccountBalance(accountId: string): Promise<MigrationBalance & { accountId: string }> {
    return this.get<MigrationBalance & { accountId: string }>(
      this.config.licensingBaseUrl,
      `/v1/accounts/${encodeURIComponent(accountId)}/balance`,
    )
  }

  // ATC-148 / `ATC-ADR-013` R-ATC-12. Per-migration consumption over a window —
  // the invoicing read, and the reconciliation surface for the epic's known
  // revenue leak (a device burn lost to two failed settles is released by the
  // wave settle and shows up here as a `burnedCount` below the wave's completed
  // device count).
  //
  // NAMED ARGUMENTS for the window, on the ATC-125 precedent: `from` and `to`
  // are two same-typed strings and are interchangeable at the type level, so a
  // positional pair is exactly the shape that let a swap through last time. The
  // server does refuse an inverted window with `window_inverted`, but a refusal
  // is a worse guard than a signature that cannot express the mistake.
  //
  // BOTH BOUNDS ARE REQUIRED and there is deliberately no default: the server
  // refuses an omitted bound rather than meaning "everything", because an
  // unbounded read returned to a caller who believed they asked for one month
  // is a confidently wrong invoice. There is likewise no `anchor` parameter —
  // the window anchors on `reservedAt` server-side so that a statement is
  // RE-RUNNABLE and two operators cannot produce two statements for one month.
  getAccountUsage(
    accountId: string,
    period: { from: string; to: string },
  ): Promise<AccountUsageResponse> {
    const params = new URLSearchParams({ from: period.from, to: period.to })
    return this.get<AccountUsageResponse>(
      this.config.licensingBaseUrl,
      `/v1/accounts/${encodeURIComponent(accountId)}/usage?${params.toString()}`,
    )
  }

  // ATC-122. A DOWNWARD correction, appended as a new ledger row rather than
  // applied as a mutation — the trail must show the mistake and the fix.
  // `amount` must be negative (the additive path is grantMigrationLicense) and
  // `reason` is mandatory: a balance reduction with no stated cause is
  // indistinguishable from a defect six months later.
  createAdjustment(args: {
    accountId: string
    licenseType: MigrationLicenseType
    amount: number
    reason: string
  }): Promise<AdjustmentResponse> {
    return this.request<AdjustmentResponse>(
      this.config.licensingBaseUrl,
      'POST',
      `/v1/accounts/${encodeURIComponent(args.accountId)}/adjustments`,
      { licenseType: args.licenseType, amount: args.amount, reason: args.reason },
    )
  }

  grantMigrationLicense(
    accountId: string,
    licenseType: MigrationLicenseType,
    amount: number,
  ): Promise<GrantResponse> {
    return this.request<GrantResponse>(
      this.config.licensingBaseUrl,
      'POST',
      `/v1/accounts/${encodeURIComponent(accountId)}/grants`,
      { licenseType, amount },
    )
  }

  getActivity(query: ActivityQuery = {}): Promise<ActivityResponse> {
    const params = new URLSearchParams()
    if (query.customerEnvironmentId) params.set('customerEnvironmentId', query.customerEnvironmentId)
    if (query.hours) params.set('hours', String(query.hours))
    if (query.limit) params.set('limit', String(query.limit))
    const qs = params.toString()
    return this.get<ActivityResponse>(this.config.licensingBaseUrl, `/v1/activity${qs ? `?${qs}` : ''}`)
  }
}
